import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, CheckCircle, CurrencyEur, Info, EnvelopeSimple, SignIn, ArrowRight, Globe } from "@phosphor-icons/react";

export default function Activate() {
    const { t, user, checkAuth } = useApp();
    const nav = useNavigate();
    const loc = useLocation();
    const [sp] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [checkout, setCheckout] = useState(null); // {url, mock, payment_id}
    const [processing, setProcessing] = useState(false);

    const isSuccess = loc.pathname.endsWith("/success");
    const isMockCheckout = loc.pathname.endsWith("/mock-checkout");
    const pid = sp.get("pid");

    // On landing at /activate/success, mark paid — but stay on the thank-you
    // page (do not auto-redirect). Digistore24 requires the buyer to see a
    // clear confirmation of what they bought and how to access it.
    useEffect(() => {
        if (isSuccess && pid) {
            setProcessing(true);
            (async () => {
                try {
                    await api.post("/payment/mark-paid", { payment_id: pid });
                    await checkAuth();
                } catch (e) {
                    // Silent — user may not be logged in yet after coming back
                    // from Digistore24. The IPN webhook will still activate them.
                } finally { setProcessing(false); }
            })();
        }
    }, [isSuccess, pid]);

    const startCheckout = async () => {
        setLoading(true);
        try {
            const r = await api.post("/payment/checkout");
            setCheckout(r.data);
            // If it's the real Digistore24, redirect immediately.
            if (!r.data.mock) {
                window.location.href = r.data.checkout_url;
            } else {
                // Show inline mock confirmation panel
                window.location.href = r.data.checkout_url;
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fehler");
        } finally { setLoading(false); }
    };

    // Mock checkout confirmation UI (renders when at /activate/mock-checkout)
    if (isMockCheckout && pid) {
        return (
            <AppShell>
                <div className="mx-auto max-w-xl px-6 lg:px-10 py-16 lg:py-24">
                    <div className="card-soft p-8 lg:p-10" data-testid="mock-checkout-panel">
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-shield-forest text-white">
                            <CurrencyEur size={26} weight="duotone" aria-hidden="true" />
                        </div>
                        <h1 className="mt-6 font-heading font-bold text-shield-ink text-2xl sm:text-3xl">Digistore24 · Testmodus</h1>
                        <p className="mt-3 text-shield-muted leading-relaxed">
                            In der Produktion würde hier die Digistore24-Checkout-Seite für dein 1 €/Monat Abo laden.
                            Für den Testmodus kannst du die Aktivierung direkt bestätigen.
                        </p>
                        <div className="mt-5 flex items-start gap-2 text-shield-terracottaDeep text-sm p-3 rounded-lg bg-shield-terracotta/10 border border-shield-terracotta/30">
                            <Info size={16} weight="fill" aria-hidden="true" className="mt-0.5" />
                            <span>{t("activate.mockNote")}</span>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => nav(`/activate/success?pid=${pid}`)}
                                className="flex-1 h-14 pill bg-shield-forest text-white text-lg font-semibold hover:bg-shield-forestDeep transition-colors"
                                data-testid="mock-confirm-btn"
                            >
                                Abo bestätigen (1 €/Monat)
                            </button>
                            <button
                                onClick={() => nav("/dashboard")}
                                className="h-14 px-5 pill border border-shield-line text-shield-ink font-semibold hover:bg-shield-sage transition-colors"
                                data-testid="mock-cancel-btn"
                            >
                                {t("common.cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            </AppShell>
        );
    }

    if (isSuccess) {
        return (
            <AppShell>
                <div className="mx-auto max-w-2xl px-6 lg:px-10 py-14 lg:py-20">
                    <div className="card-soft p-8 lg:p-12" data-testid="activate-success">
                        {/* Check icon + headline */}
                        <div className="text-center">
                            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-shield-success text-white shadow-lift">
                                <CheckCircle size={32} weight="fill" aria-hidden="true" />
                            </div>
                            <h1 className="mt-6 font-heading font-bold text-shield-ink text-3xl sm:text-4xl leading-tight" data-testid="success-title">
                                {t("activate.thankTitle")}
                            </h1>
                            <p className="mt-4 text-lg text-shield-muted leading-relaxed max-w-xl mx-auto" data-testid="success-body">
                                {t("activate.thankBody")}
                            </p>

                            {/* Digistore24 mandatory billing notice — prominent, right under the headline */}
                            <div
                                className="mt-6 inline-flex items-center gap-2 h-10 px-4 pill bg-shield-forest/10 border border-shield-forest/30 text-shield-forest font-semibold text-sm"
                                data-testid="success-billing-badge"
                            >
                                <CurrencyEur size={16} weight="bold" aria-hidden="true" />
                                {t("activate.billingNote")}
                            </div>
                        </div>

                        {/* Product delivery info — Digistore24 requirement "Hinweis wie Käufer Produkt erhält" */}
                        <div
                            className="mt-8 p-5 rounded-2xl bg-shield-forest/5 border-2 border-shield-forest/20"
                            data-testid="success-delivery"
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-shield-forest text-white">
                                    <Globe size={20} weight="duotone" aria-hidden="true" />
                                </div>
                                <div>
                                    <div className="font-heading font-semibold text-shield-ink">
                                        {t("activate.deliveryTitle")}
                                    </div>
                                    <p className="mt-1 text-sm text-shield-ink leading-relaxed">
                                        {t("activate.deliveryBody")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Two-step access instructions */}
                        <div className="mt-6 space-y-4">
                            <div className="flex items-start gap-4 p-5 rounded-2xl bg-shield-sage/50 border border-shield-line" data-testid="success-step-1">
                                <div className="flex-shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-shield-forest text-white">
                                    <EnvelopeSimple size={22} weight="duotone" aria-hidden="true" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-forest">
                                        {t("activate.step1Title")}
                                    </div>
                                    <p className="mt-1 text-shield-ink font-medium leading-relaxed">
                                        {t("activate.step1Body")}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 p-5 rounded-2xl bg-shield-sage/50 border border-shield-line" data-testid="success-step-2">
                                <div className="flex-shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-shield-terracotta text-white">
                                    <SignIn size={22} weight="duotone" aria-hidden="true" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                                        {t("activate.step2Title")}
                                    </div>
                                    <p className="mt-1 text-shield-ink font-medium leading-relaxed">
                                        {t("activate.step2Body")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Prominent CTA */}
                        <div className="mt-10 text-center">
                            <button
                                onClick={() => nav(user ? "/dashboard" : "/login")}
                                className="group inline-flex items-center gap-2 h-14 px-8 pill bg-shield-forest text-white text-lg font-semibold shadow-lift hover:bg-shield-forestDeep transition-colors active:scale-[0.98]"
                                data-testid="success-dashboard-btn"
                            >
                                {t("activate.dashboardBtn")}
                                <ArrowRight size={20} weight="bold" className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                            </button>
                            <p className="mt-4 text-sm font-semibold text-shield-forest" data-testid="success-billing-btnrow">
                                {t("activate.billingNote")}
                            </p>
                            {processing && (
                                <p className="mt-2 text-xs text-shield-muted">{t("activate.processing")}</p>
                            )}
                        </div>

                        {/* Mandatory billing footnote */}
                        <div className="mt-10 pt-6 border-t border-shield-line text-center">
                            <p className="text-sm text-shield-muted" data-testid="success-billing-note">
                                {t("activate.billingNote")}
                            </p>
                        </div>
                    </div>
                </div>
            </AppShell>
        );
    }

    if (user?.activated) {
        return (
            <AppShell>
                <div className="mx-auto max-w-xl px-6 lg:px-10 py-16 lg:py-24 text-center">
                    <div className="card-soft p-10">
                        <CheckCircle size={40} weight="fill" className="mx-auto text-shield-success" aria-hidden="true" />
                        <h1 className="mt-4 font-heading font-bold text-shield-ink text-2xl">{t("dashboard.activated")}</h1>
                        <button
                            onClick={() => nav("/dashboard")}
                            className="mt-6 h-12 px-6 pill bg-shield-forest text-white font-semibold hover:bg-shield-forestDeep transition-colors"
                            data-testid="activate-back-btn"
                        >
                            {t("nav.dashboard")}
                        </button>
                    </div>
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="mx-auto max-w-2xl px-6 lg:px-10 py-16 lg:py-24">
                <div className="text-center">
                    <div className="inline-flex items-center gap-2 h-8 px-3 pill bg-white border border-shield-line text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                        <Sparkle size={14} weight="fill" aria-hidden="true" /> Einmalig
                    </div>
                    <h1 className="mt-4 font-heading font-bold text-shield-ink text-3xl sm:text-4xl lg:text-5xl" data-testid="activate-title">
                        {t("activate.title")}
                    </h1>
                    <p className="mt-3 text-shield-muted text-lg">{t("activate.sub")}</p>
                </div>

                <div className="mt-10 card-soft p-10 text-center">
                    <div className="flex items-baseline justify-center gap-1">
                        <span className="font-heading font-bold text-shield-forest text-7xl leading-none">1€</span>
                        <span className="text-shield-muted text-lg font-medium">{t("activate.perMonth")}</span>
                    </div>
                    <div className="mt-2 text-shield-muted">via Digistore24 · jederzeit kündbar</div>

                    <button
                        onClick={startCheckout}
                        disabled={loading}
                        className="mt-8 inline-flex items-center gap-2 h-14 px-8 pill bg-shield-terracotta text-white text-lg font-semibold shadow-lift hover:bg-shield-terracottaDeep transition-colors disabled:opacity-70"
                        data-testid="activate-checkout-btn"
                    >
                        {loading ? t("common.loading") : t("activate.checkout")}
                    </button>

                    {checkout?.mock && (
                        <p className="mt-4 text-xs text-shield-muted">{t("activate.mockNote")}</p>
                    )}
                </div>
            </div>
        </AppShell>
    );
}
